import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import {
  useListTrainers,
  useUpdateTrainerAdminSettings,
  useListLocations,
  useInviteTrainer,
  useListTrainerInvites,
  useResendTrainerInvite,
  useCancelTrainerInvite,
  useDeleteTrainer,
  getListTrainersQueryKey,
  getListTrainerInvitesQueryKey,
  getGetTrainerAvailabilityQueryOptions,
  TrainerProfile,
  Location,
  TrainerInvite,
} from "@workspace/api-client-react";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { formatCurrency } from "@/lib/utils";
import { Users, Plus, Star, Dumbbell, Pencil, DollarSign, Percent, Mail, RotateCw, Clock, CheckCircle, XCircle, Send, Trash2, X, AlertTriangle, MapPin, ChevronDown, ChevronUp, TrendingUp, Repeat2, UserCheck, LogIn } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

type InviteTrainerForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type EditSettingsForm = {
  hourlyRate: string;
  gymSplitPct: string;
  canSetOwnRate: boolean;
  primaryLocationId: string;
  locationIds: string[];
  gender: string;
};


const INITIAL_INVITE_FORM: InviteTrainerForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

export default function AdminTrainers() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const gymId = user?.gymId ?? "";

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<TrainerProfile | null>(null);
  const [inviteForm, setInviteForm] = useState<InviteTrainerForm>(INITIAL_INVITE_FORM);
  const [editForm, setEditForm] = useState<EditSettingsForm>({
    hourlyRate: "",
    gymSplitPct: "",
    canSetOwnRate: false,
    primaryLocationId: "none",
    locationIds: [],
    gender: "",
  });

  const { data: trainers, isLoading } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });
  const { data: locations } = useListLocations(gymId, { query: { enabled: !!gymId } });
  const { data: invites } = useListTrainerInvites({ query: { enabled: !!gymId } });

  const [deliveryWarnings, setDeliveryWarnings] = useState<string[] | null>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState<TrainerProfile | null>(null);
  const [selectedTrainerIds, setSelectedTrainerIds] = useState<Set<string>>(new Set());
  const [, setNav] = useLocation();

  const [openInsightsId, setOpenInsightsId] = useState<string | null>(null);
  const [insightsCache, setInsightsCache] = useState<Record<string, any>>({});
  const [insightsLoading, setInsightsLoading] = useState<Record<string, boolean>>({});

  const toggleInsights = async (trainerId: string) => {
    if (openInsightsId === trainerId) { setOpenInsightsId(null); return; }
    setOpenInsightsId(trainerId);
    if (insightsCache[trainerId]) return;
    setInsightsLoading(prev => ({ ...prev, [trainerId]: true }));
    try {
      const data = await apiFetch(`/admin/trainers/${trainerId}/insights`);
      setInsightsCache(prev => ({ ...prev, [trainerId]: data }));
    } catch { /* silent */ }
    finally { setInsightsLoading(prev => ({ ...prev, [trainerId]: false })); }
  };

  const toggleTrainerSelect = (id: string) => {
    setSelectedTrainerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllTrainers = () => {
    if (trainerList.every(t => selectedTrainerIds.has(t.id))) {
      setSelectedTrainerIds(new Set());
    } else {
      setSelectedTrainerIds(new Set(trainerList.map(t => t.id)));
    }
  };

  const handleSendMessageToTrainers = () => {
    const ids = Array.from(selectedTrainerIds).join(",");
    setNav(`/admin/messaging?recipients=${ids}&role=TRAINER`);
  };

  const inviteTrainer = useInviteTrainer();
  const resendInvite = useResendTrainerInvite();
  const cancelInvite = useCancelTrainerInvite();
  const deleteTrainer = useDeleteTrainer();
  const updateSettings = useUpdateTrainerAdminSettings();

  const invalidateTrainers = () => {
    queryClient.invalidateQueries({ queryKey: getListTrainersQueryKey(gymId) });
  };

  const invalidateInvites = () => {
    queryClient.invalidateQueries({ queryKey: getListTrainerInvitesQueryKey() });
  };

  const handleInvite = async () => {
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    try {
      const result = await inviteTrainer.mutateAsync({
        data: {
          firstName: inviteForm.firstName,
          lastName: inviteForm.lastName,
          email: inviteForm.email,
          phone: inviteForm.phone || undefined,
        },
      });
      invalidateInvites();
      setInviteOpen(false);
      setInviteForm(INITIAL_INVITE_FORM);
      const warnings = (result as unknown as Record<string, unknown>).warnings as string[] | undefined;
      if (warnings && warnings.length > 0) {
        setDeliveryWarnings(warnings.map(w => {
          const m = w.match(/^(.*?)\s*\[Detail:.*?\]$/);
          return m ? m[1].trim() : w;
        }));
      } else {
        toast({ title: "Invite sent successfully" });
      }
    } catch (e: unknown) {
      const apiError = e instanceof Error && "data" in e ? (e as { data: unknown }).data : null;
      const msg =
        apiError && typeof apiError === "object" && "error" in apiError && typeof (apiError as { error: unknown }).error === "string"
          ? (apiError as { error: string }).error
          : "Failed to send invite";
      invalidateInvites();
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleResend = async (token: string) => {
    try {
      const result = await resendInvite.mutateAsync({ token });
      invalidateInvites();
      const warnings = (result as unknown as Record<string, unknown>).warnings as string[] | undefined;
      if (warnings && warnings.length > 0) {
        setDeliveryWarnings(warnings.map(w => {
          const m = w.match(/^(.*?)\s*\[Detail:.*?\]$/);
          return m ? m[1].trim() : w;
        }));
      } else {
        toast({ title: "Invite resent successfully" });
      }
    } catch (e: unknown) {
      const apiError = e instanceof Error && "data" in e ? (e as { data: unknown }).data : null;
      const msg =
        apiError && typeof apiError === "object" && "error" in apiError && typeof (apiError as { error: unknown }).error === "string"
          ? (apiError as { error: string }).error
          : "Failed to resend invite";
      invalidateInvites();
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleCancelInvite = async (token: string) => {
    try {
      await cancelInvite.mutateAsync({ token });
      invalidateInvites();
      toast({ title: "Invite cancelled successfully" });
    } catch {
      toast({ title: "Failed to cancel invite", variant: "destructive" });
    }
  };

  const openDeleteConfirm = (trainer: TrainerProfile) => {
    setTrainerToDelete(trainer);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTrainer = async () => {
    if (!trainerToDelete) return;
    try {
      await deleteTrainer.mutateAsync({ id: trainerToDelete.id });
      invalidateTrainers();
      setDeleteConfirmOpen(false);
      setTrainerToDelete(null);
      toast({ title: "Trainer removed successfully" });
    } catch {
      toast({ title: "Failed to remove trainer", variant: "destructive" });
    }
  };

  async function impersonate(userId: string) {
    try {
      const result = await apiFetch<{ token?: string }>(`/admin/impersonate/${userId}`, { method: "POST" });
      if (result.token) {
        localStorage.setItem("originalToken", localStorage.getItem("token") || "");
        localStorage.setItem("impersonating", "true");
        localStorage.setItem("token", result.token);
        window.location.href = "/trainer/dashboard";
      }
    } catch (e: any) {
      toast({ title: "Could not impersonate", description: e.message, variant: "destructive" });
    }
  }

  const openEdit = (trainer: TrainerProfile) => {
    setSelectedTrainer(trainer);
    setEditForm({
      hourlyRate: trainer.hourlyRate != null ? String(trainer.hourlyRate) : "",
      gymSplitPct: trainer.gymSplitPct != null ? String(trainer.gymSplitPct) : "",
      canSetOwnRate: trainer.canSetOwnRate,
      primaryLocationId: trainer.primaryLocationId ?? "none",
      locationIds: trainer.locationIds ?? [],
      gender: trainer.gender || "",
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedTrainer) return;
    try {
      await updateSettings.mutateAsync({
        id: selectedTrainer.id,
        data: {
          hourlyRate: editForm.hourlyRate ? parseFloat(editForm.hourlyRate) : undefined,
          gymSplitPct: editForm.gymSplitPct ? parseFloat(editForm.gymSplitPct) : undefined,
          canSetOwnRate: editForm.canSetOwnRate,
          primaryLocationId: editForm.primaryLocationId !== "none" ? editForm.primaryLocationId : undefined,
          locationIds: editForm.locationIds,
          gender: editForm.gender || undefined,
        },
      });
      invalidateTrainers();
      setEditOpen(false);
      setSelectedTrainer(null);
      toast({ title: "Trainer settings updated" });
    } catch {
      toast({ title: "Failed to update settings", variant: "destructive" });
    }
  };

  const trainerList: TrainerProfile[] = trainers ?? [];
  const locationList: Location[] = locations ?? [];
  const inviteList: TrainerInvite[] = invites ?? [];

  const availabilityQueries = useQueries({
    queries: trainerList.map((trainer) => ({
      ...getGetTrainerAvailabilityQueryOptions(trainer.id),
      enabled: !!trainer.id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const trainersWithAvailability = useMemo(() => {
    const set = new Set<string>();
    availabilityQueries.forEach((q, i) => {
      if (q.data && Array.isArray(q.data) && q.data.length > 0) {
        set.add(trainerList[i]?.id);
      }
    });
    return set;
  }, [availabilityQueries, trainerList]);

  const getStatusBadge = (invite: TrainerInvite) => {
    const now = new Date();
    const expired = invite.status === "EXPIRED" || (invite.status === "PENDING" && new Date(invite.expiresAt) < now);
    if (invite.status === "ACCEPTED") {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" /> Accepted</Badge>;
    }
    if (expired) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" /> Expired</Badge>;
    }
    return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  };


  const canResend = (invite: TrainerInvite) => {
    if (invite.status === "ACCEPTED") return false;
    return true;
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Trainers</h1>
            <p className="text-muted-foreground text-lg">Manage your gym's training staff.</p>
          </div>
          <div className="flex items-center gap-3">
            {selectedTrainerIds.size > 0 && (
              <Button variant="outline" onClick={handleSendMessageToTrainers} className="gap-2">
                <Send className="w-4 h-4" /> Send Message ({selectedTrainerIds.size})
              </Button>
            )}
            <Button onClick={() => setInviteOpen(true)} className="gap-2">
              <Send className="w-4 h-4" /> Invite Trainer
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 glass-panel rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : trainerList.length === 0 && inviteList.length === 0 ? (
          <Card className="glass-panel border-white/5">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-4">
              <Users className="w-12 h-12 text-muted-foreground" />
              <p className="text-muted-foreground">No trainers yet. Invite your first trainer to get started.</p>
              <Button onClick={() => setInviteOpen(true)} className="gap-2">
                <Send className="w-4 h-4" /> Invite First Trainer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {trainerList.length > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={trainerList.length > 0 && trainerList.every(t => selectedTrainerIds.has(t.id))}
                    onCheckedChange={toggleAllTrainers}
                  />
                  <span className="text-xs text-muted-foreground">Select All ({trainerList.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {trainerList.map((trainer) => (
                  <Card key={trainer.id} className={`glass-panel flex flex-col ${trainersWithAvailability.has(trainer.id) ? "border-2 border-yellow-400" : "border-white/5"} ${selectedTrainerIds.has(trainer.id) ? "ring-1 ring-primary" : ""}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedTrainerIds.has(trainer.id)}
                            onCheckedChange={() => toggleTrainerSelect(trainer.id)}
                          />
                          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-lg font-bold border border-white/10 shrink-0">
                            {trainer.firstName[0]}{trainer.lastName[0]}
                          </div>
                          <div>
                            <CardTitle className="text-white text-base leading-tight">
                              {trainer.firstName} {trainer.lastName}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">{trainer.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8 text-muted-foreground hover:text-amber-400 hover:bg-amber-400/10" onClick={() => impersonate(trainer.id)} title="View as this trainer">
                            <LogIn className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={() => openEdit(trainer)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => openDeleteConfirm(trainer)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4 flex-1">
                      {trainer.locationIds && trainer.locationIds.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {trainer.locationIds.map((locId: string) => {
                            const loc = locationList.find(l => l.id === locId);
                            return loc ? (
                              <Badge key={locId} variant="secondary" className="text-xs bg-primary/10 border-primary/20 text-primary">
                                <MapPin className="w-3 h-3 mr-0.5" />{loc.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                      {trainer.specialties && trainer.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {trainer.specialties.map((s: string) => (
                            <Badge key={s} variant="secondary" className="text-xs bg-white/5 border-white/10 text-muted-foreground">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3 mt-auto">
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <DollarSign className="w-3.5 h-3.5 text-primary" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Rate/hr</span>
                          </div>
                          <p className="text-white font-mono font-bold text-sm">
                            {trainer.hourlyRate != null ? formatCurrency(trainer.hourlyRate) : "\u2014"}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Percent className="w-3.5 h-3.5 text-primary" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Gym Split</span>
                          </div>
                          <p className="text-white font-mono font-bold text-sm">
                            {trainer.gymSplitPct != null ? `${trainer.gymSplitPct}%` : "\u2014"}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Star className="w-3.5 h-3.5 text-primary" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Rating</span>
                          </div>
                          <p className="text-white font-mono font-bold text-sm">
                            {trainer.avgRating != null ? trainer.avgRating.toFixed(1) : "\u2014"}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Dumbbell className="w-3.5 h-3.5 text-primary" />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Sessions</span>
                          </div>
                          <p className="text-white font-mono font-bold text-sm">{trainer.totalSessions}</p>
                        </div>
                      </div>
                      {trainer.canSetOwnRate && (
                        <p className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider">Can set own rate</p>
                      )}

                      <button
                        onClick={() => toggleInsights(trainer.id)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 text-xs text-muted-foreground hover:text-white transition-colors"
                      >
                        <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wider">
                          <TrendingUp className="w-3.5 h-3.5" /> Student Insights
                        </span>
                        {openInsightsId === trainer.id
                          ? <ChevronUp className="w-3.5 h-3.5" />
                          : <ChevronDown className="w-3.5 h-3.5" />
                        }
                      </button>

                      {openInsightsId === trainer.id && (
                        <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 space-y-4">
                          {insightsLoading[trainer.id] ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : insightsCache[trainer.id] ? (() => {
                            const ins = insightsCache[trainer.id];
                            return (
                              <>
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="flex flex-col items-center p-2 rounded-lg bg-white/[0.04] border border-white/5">
                                    <Users className="w-3.5 h-3.5 text-blue-400 mb-1" />
                                    <p className="text-white font-mono font-bold text-sm">{ins.totalClients}</p>
                                    <p className="text-[10px] text-muted-foreground">Total</p>
                                  </div>
                                  <div className="flex flex-col items-center p-2 rounded-lg bg-white/[0.04] border border-white/5">
                                    <Repeat2 className="w-3.5 h-3.5 text-emerald-400 mb-1" />
                                    <p className="text-white font-mono font-bold text-sm">{ins.returningClients}</p>
                                    <p className="text-[10px] text-muted-foreground">Returning</p>
                                  </div>
                                  <div className="flex flex-col items-center p-2 rounded-lg bg-white/[0.04] border border-white/5">
                                    <UserCheck className="w-3.5 h-3.5 text-primary mb-1" />
                                    <p className={cn("font-mono font-bold text-sm", ins.retentionRate >= 50 ? "text-emerald-400" : "text-amber-400")}>
                                      {ins.retentionRate}%
                                    </p>
                                    <p className="text-[10px] text-muted-foreground">Retention</p>
                                  </div>
                                </div>

                                <div>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Retention Rate</span>
                                    <span className="text-[10px] text-muted-foreground">{ins.avgSessionsPerClient} avg sessions/client</span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                      className={cn("h-full rounded-full transition-all", ins.retentionRate >= 50 ? "bg-emerald-400" : "bg-amber-400")}
                                      style={{ width: `${ins.retentionRate}%` }}
                                    />
                                  </div>
                                </div>

                                {ins.topClients.length > 0 && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Top Recurring Clients</p>
                                    <div className="space-y-1.5">
                                      {ins.topClients.map((c: any, idx: number) => (
                                        <div key={c.studentId} className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-mono text-muted-foreground w-4">#{idx + 1}</span>
                                            <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center text-[9px] font-bold border border-white/10">
                                              {c.studentName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)}
                                            </div>
                                            <span className="text-xs text-white truncate max-w-[100px]">{c.studentName}</span>
                                          </div>
                                          <span className="text-xs font-mono text-primary font-semibold shrink-0">
                                            {c.sessionCount} {c.sessionCount === 1 ? "session" : "sessions"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {ins.totalClients === 0 && (
                                  <p className="text-xs text-muted-foreground text-center py-2">No completed sessions yet.</p>
                                )}
                              </>
                            );
                          })() : (
                            <p className="text-xs text-muted-foreground text-center py-2">Failed to load insights.</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              </>
            )}

            {inviteList.length > 0 && (
              <div>
                <h2 className="text-xl font-display font-bold text-white mb-4">Pending Invites</h2>
                <div className="space-y-3">
                  {inviteList.map((invite) => (
                    <Card key={invite.id} className="glass-panel border-white/5">
                      <CardContent className="flex items-center justify-between py-4 px-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-sm font-bold border border-white/10 shrink-0">
                            <Mail className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-white font-semibold text-sm flex items-center gap-2">
                              {invite.recipientFirstName} {invite.recipientLastName}
                              {(invite.emailDelivered === false || invite.smsDelivered === false) && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 text-amber-400">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs text-xs">
                                      <p className="font-semibold mb-1">Delivery issues on last attempt:</p>
                                      {invite.emailDelivered === false && <p>Email delivery failed</p>}
                                      {invite.smsDelivered === false && <p>SMS delivery failed</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{invite.recipientEmail}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(invite)}
                          {canResend(invite) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1.5 text-xs"
                                onClick={() => handleResend(invite.token)}
                                disabled={resendInvite.isPending}
                              >
                                <RotateCw className="w-3 h-3" />
                                Resend
                              </Button>
                              {canResend(invite) && invite.status === "PENDING" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => handleCancelInvite(invite.token)}
                                  disabled={cancelInvite.isPending}
                                >
                                  <X className="w-3 h-3" />
                                  Cancel
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">Invite Trainer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  className="glass-panel border-white/10"
                  value={inviteForm.firstName}
                  onChange={e => setInviteForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  className="glass-panel border-white/10"
                  value={inviteForm.lastName}
                  onChange={e => setInviteForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                className="glass-panel border-white/10"
                type="email"
                value={inviteForm.email}
                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                placeholder="trainer@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                className="glass-panel border-white/10"
                value={inviteForm.phone}
                onChange={e => setInviteForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+1 (555) 000-0000"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The trainer will receive an invite via email (and SMS if phone provided) with a link to set up their account, fill in their profile, and connect their bank account for payouts.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviteTrainer.isPending} className="gap-2">
              {inviteTrainer.isPending ? "Sending..." : <><Send className="w-4 h-4" /> Send Invite</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide">
              Edit Settings \u2014 {selectedTrainer?.firstName} {selectedTrainer?.lastName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  className="glass-panel border-white/10"
                  type="number"
                  value={editForm.hourlyRate}
                  onChange={e => setEditForm(f => ({ ...f, hourlyRate: e.target.value }))}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label>Gym Split (%)</Label>
                <Input
                  className="glass-panel border-white/10"
                  type="number"
                  value={editForm.gymSplitPct}
                  onChange={e => setEditForm(f => ({ ...f, gymSplitPct: e.target.value }))}
                  placeholder="30"
                />
              </div>
            </div>
            {locationList.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Locations</Label>
                <div className="space-y-2 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                  {locationList.filter(l => l.isActive).map((loc) => (
                    <div key={loc.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`loc-${loc.id}`}
                        checked={editForm.locationIds.includes(loc.id)}
                        onCheckedChange={(checked) => {
                          setEditForm(f => {
                            const newIds = checked
                              ? [...f.locationIds, loc.id]
                              : f.locationIds.filter(id => id !== loc.id);
                            const newPrimary = !checked && f.primaryLocationId === loc.id
                              ? (newIds[0] ?? "none")
                              : f.primaryLocationId;
                            return { ...f, locationIds: newIds, primaryLocationId: newPrimary };
                          });
                        }}
                      />
                      <label htmlFor={`loc-${loc.id}`} className="text-sm text-white cursor-pointer">{loc.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {editForm.locationIds.length > 1 && (
              <div className="space-y-2">
                <Label>Primary Location (default display)</Label>
                <Select
                  value={editForm.primaryLocationId}
                  onValueChange={v => setEditForm(f => ({ ...f, primaryLocationId: v }))}
                >
                  <SelectTrigger className="glass-panel border-white/10">
                    <SelectValue placeholder="Select primary" />
                  </SelectTrigger>
                  <SelectContent>
                    {editForm.locationIds.map(id => {
                      const loc = locationList.find(l => l.id === id);
                      return loc ? <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem> : null;
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5">
              <div>
                <p className="text-sm font-semibold text-white">Can Set Own Rate</p>
                <p className="text-xs text-muted-foreground mt-0.5">Allow trainer to override their hourly rate</p>
              </div>
              <Switch
                checked={editForm.canSetOwnRate}
                onCheckedChange={v => setEditForm(f => ({ ...f, canSetOwnRate: v }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <select
                value={editForm.gender}
                onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}
                className="w-full bg-background border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              >
                <option value="">Not specified</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
              <p className="text-xs text-muted-foreground">Used for scheduling assistant trainer matching.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={updateSettings.isPending}>
              {updateSettings.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wide flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Remove Trainer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to remove <span className="text-white font-semibold">{trainerToDelete?.firstName} {trainerToDelete?.lastName}</span> from your gym?
            </p>
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 space-y-2">
              <p className="text-sm font-semibold text-red-400">This action will:</p>
              <ul className="text-xs text-red-300/80 space-y-1 list-disc list-inside">
                <li>Cancel all upcoming confirmed and pending sessions</li>
                <li>Deactivate all availability blocks</li>
                <li>Remove the trainer's association with your gym</li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground">
              This action cannot be easily undone. The trainer's historical data (past sessions, ratings, payouts) will be preserved.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setTrainerToDelete(null); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTrainer}
              disabled={deleteTrainer.isPending}
              className="gap-2"
            >
              {deleteTrainer.isPending ? "Removing..." : <><Trash2 className="w-4 h-4" /> Remove Trainer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deliveryWarnings} onOpenChange={(open) => { if (!open) setDeliveryWarnings(null); }}>
        <AlertDialogContent className="glass-panel border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Delivery Warning
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>The invite was created, but there were delivery issues:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {deliveryWarnings?.map((w, i) => (
                    <li key={i} className="text-sm text-muted-foreground">{w}</li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">
                  Check your email service (Resend) and SMS service (Twilio) configuration to resolve delivery issues. You can resend the invite once the issue is fixed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
